import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import {
  FaTags,
  FaPlus,
  FaEdit,
  FaSearch,
  FaArrowUp,
  FaArrowDown,
  FaTrash,
  FaTrashRestore,
  FaChevronLeft
} from "react-icons/fa";

export default function ViewCategory() {
  const [loading, setloading] = useState(true);
  const [categoryList, setcategoryList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  // Deleted categories are listed separately and can be restored, like products.
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const fetchCategories = useCallback(() => {
    setloading(true);
    axios.get(`/api/view-category${showDeleted ? "?deleted=1" : ""}`).then((res) => {
      if (res.data.status === 200) {
        setcategoryList(res.data.category || []);
        setDeletedCount(res.data.deleted_count || 0);
      }
      setloading(false);
    }).catch(() => {
      setloading(false);
    });
  }, [showDeleted]);

  useEffect(() => {
    document.title = showDeleted ? "Deleted Categories — Admin" : "Manage Categories — Admin";
    fetchCategories();
  }, [fetchCategories, showDeleted]);

  const deleteCategory = (item) => {
    const count = Number(item.products_count || 0);
    swal({
      title: `Delete "${item.name}"?`,
      text: count
        ? `Its ${count} product${count === 1 ? "" : "s"} will be hidden from the store, not deleted. You can restore the category any time from Deleted.`
        : "You can restore it any time from Deleted.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      setBusyId(item.id);
      axios.delete(`/api/delete-category/${item.id}`)
        .then((res) => {
          if (res.data.status === 200) {
            swal("Deleted", res.data.message, "success");
            fetchCategories();
          } else {
            swal("Error", res.data.message || "Could not delete the category", "error");
          }
        })
        .catch(() => swal("Error", "Could not delete the category", "error"))
        .finally(() => setBusyId(null));
    });
  };

  const restoreCategory = (item) => {
    setBusyId(item.id);
    axios.put(`/api/restore-category/${item.id}`)
      .then((res) => {
        if (res.data.status === 200) {
          swal("Restored", res.data.message, "success");
          fetchCategories();
        } else {
          swal("Error", res.data.message || "Could not restore the category", "error");
        }
      })
      .catch(() => swal("Error", "Could not restore the category", "error"))
      .finally(() => setBusyId(null));
  };

  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const toggleCategoryStatus = (id) => {
    setUpdatingStatusId(id);
    axios
      .put(`/api/toggle-category-status/${id}`)
      .then((res) => {
        setUpdatingStatusId(null);
        if (res.data.status === 200) {
          swal("Updated", res.data.message || "Category visibility updated", "success");
          setcategoryList((prev) =>
            prev.map((cat) =>
              cat.id === id ? { ...cat, status: res.data.category_status } : cat
            )
          );
        } else {
          swal("Error", res.data.message || "Could not update status", "error");
        }
      })
      .catch(() => {
        setUpdatingStatusId(null);
        swal("Error", "Could not toggle category status", "error");
      });
  };

  const moveCategory = (id, direction) => {
    if (searchQuery.trim() || savingOrder) return;
    const index = categoryList.findIndex((item) => item.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= categoryList.length) return;
    const previous = categoryList;
    const next = [...categoryList];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved);
    setcategoryList(next);
    setSavingOrder(true);
    axios
      .post("/api/reorder-categories", { ids: next.map((item) => item.id) })
      .then((res) => {
        setSavingOrder(false);
        if (res.data.status !== 200) {
          setcategoryList(previous);
          swal("Error", res.data.message || "Could not save the order", "error");
        }
      })
      .catch(() => {
        setSavingOrder(false);
        setcategoryList(previous);
        swal("Error", "Could not save the order", "error");
      });
  };

  const filtered = categoryList.filter((item) => {
    const q = searchQuery.toLowerCase();
    return (
      (item.name || "").toLowerCase().includes(q) ||
      (item.slug || "").toLowerCase().includes(q)
    );
  });

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            {showDeleted ? "Deleted categories" : "Categories"} ({categoryList.length})
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            {showDeleted
              ? "Restore a category to put it and its products back on the store."
              : "Hide a category to take it off the store for a while, or delete it. Use the arrows to choose which collection appears first."}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <button
            type="button"
            className={`btn rounded-pill px-3 py-2 fw-semibold d-flex align-items-center gap-2 ${showDeleted ? "btn-dark" : "btn-light border"}`}
            style={{ fontSize: "13.5px" }}
            onClick={() => {
              setSearchQuery("");
              setShowDeleted((current) => !current);
            }}
          >
            {showDeleted ? <FaChevronLeft size={11} /> : <FaTrash size={11} />}
            {showDeleted ? "Back to categories" : `Deleted${deletedCount ? ` (${deletedCount})` : ""}`}
          </button>
          {!showDeleted && (
            <Link
              to="/admin/add-category"
              className="btn btn-primary rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-2"
              style={{ fontSize: "13.5px", background: "var(--admin-primary)", border: "none" }}
            >
              <FaPlus size={12} /> Add Category
            </Link>
          )}
        </div>
      </div>

      {/* Main Card */}
      <div className="admin-card">
        <div className="admin-card-header flex-wrap gap-2">
          <div className="position-relative" style={{ maxWidth: "320px", width: "100%" }}>
            <input
              type="text"
              className="form-control form-control-sm ps-4"
              placeholder="Search category by name or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ borderRadius: "8px", fontSize: "13px" }}
            />
            <span className="position-absolute start-0 top-50 translate-middle-y ms-2 text-muted" style={{ fontSize: "11px" }}>
              <FaSearch />
            </span>
          </div>
          <span className="text-muted" style={{ fontSize: "12.5px" }}>
            {showDeleted
              ? `${filtered.length} deleted`
              : searchQuery.trim()
                ? "Clear the search before changing the order."
                : `Showing ${filtered.length} of ${categoryList.length} collections`}
          </span>
        </div>

        <div className="table-responsive">
          {loading ? (
            <div className="p-5 text-center">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-5 text-center">
              <FaTags size={36} className="text-muted mb-2" />
              <h6 className="fw-bold">{showDeleted ? "No deleted categories" : "No Categories Found"}</h6>
              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                {searchQuery
                  ? "No categories match your search."
                  : showDeleted ? "Categories you delete show here, so you can bring them back." : "No categories created yet."}
              </p>
              {!showDeleted && (
                <Link to="/admin/add-category" className="btn btn-sm btn-dark rounded-pill px-3">
                  Create First Category
                </Link>
              )}
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  {!showDeleted && <th style={{ width: "110px" }}>Order</th>}
                  <th style={{ width: "70px" }}>Image</th>
                  <th>Category Name</th>
                  <th>URL Slug</th>
                  <th>Products</th>
                  <th>{showDeleted ? "Deleted on" : "Visibility"}</th>
                  <th className="text-end" style={{ width: "190px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const position = categoryList.findIndex((row) => row.id === item.id);
                  const orderLocked = searchQuery.trim() !== "" || savingOrder;
                  return (
                  <tr key={item.id}>
                    {!showDeleted && (
                    <td>
                      <div className="d-inline-flex align-items-center gap-1">
                        <span className="fw-bold" style={{ minWidth: "18px" }}>{position + 1}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border d-flex align-items-center justify-content-center"
                          style={{ width: "28px", height: "28px" }}
                          title={searchQuery.trim() ? "Clear the search before changing the order" : "Show this category earlier"}
                          disabled={orderLocked || position <= 0}
                          onClick={() => moveCategory(item.id, -1)}
                        >
                          <FaArrowUp size={11} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border d-flex align-items-center justify-content-center"
                          style={{ width: "28px", height: "28px" }}
                          title={searchQuery.trim() ? "Clear the search before changing the order" : "Show this category later"}
                          disabled={orderLocked || position < 0 || position >= categoryList.length - 1}
                          onClick={() => moveCategory(item.id, 1)}
                        >
                          <FaArrowDown size={11} />
                        </button>
                      </div>
                    </td>
                    )}
                    <td>
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "1px solid var(--admin-border)",
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <img
                          src={getProductImageUrl(item.image)}
                          alt={item.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={handleProductImageError}
                        />
                      </div>
                    </td>
                    <td>
                      <span className="fw-bold" style={{ color: "var(--admin-text-main)" }}>
                        {item.name}
                      </span>
                    </td>
                    <td>
                      <code className="px-2 py-1 bg-light text-dark rounded" style={{ fontSize: "12px" }}>
                        /{item.slug}
                      </code>
                    </td>
                    <td>
                      <span className="text-muted" style={{ fontSize: "13px" }}>{Number(item.products_count || 0)}</span>
                    </td>
                    <td>
                      {showDeleted ? (
                        <span className="text-muted" style={{ fontSize: "13px" }}>
                          {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString() : "—"}
                        </span>
                      ) : (
                      <button
                        type="button"
                        onClick={() => toggleCategoryStatus(item.id)}
                        disabled={updatingStatusId === item.id}
                        className="btn btn-link p-0 text-decoration-none border-0"
                        title="Click to hide or show this category on the store"
                        style={{ cursor: "pointer" }}
                      >
                        {updatingStatusId === item.id ? (
                          <span className="spinner-border spinner-border-sm text-secondary" role="status"></span>
                        ) : Number(item.status) === 1 || item.status === "1" ? (
                          <span className="admin-badge admin-badge-success cursor-pointer">
                            ● Shown
                          </span>
                        ) : (
                          <span className="admin-badge admin-badge-warning cursor-pointer">
                            ○ Hidden
                          </span>
                        )}
                      </button>
                      )}
                    </td>
                    <td className="text-end">
                      <div className="d-inline-flex gap-2">
                        {showDeleted ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-success d-flex align-items-center gap-1 rounded-2"
                            style={{ fontSize: "12.5px" }}
                            disabled={busyId === item.id}
                            onClick={() => restoreCategory(item)}
                          >
                            <FaTrashRestore size={12} /> {busyId === item.id ? "Restoring…" : "Restore"}
                          </button>
                        ) : (
                          <>
                            <Link
                              to={`/admin/edit-category/${item.id}`}
                              className="btn btn-sm btn-light border d-flex align-items-center gap-1 rounded-2"
                              style={{ fontSize: "12.5px" }}
                              title="Edit Category"
                            >
                              <FaEdit size={12} /> Edit
                            </Link>
                            <button
                              type="button"
                              className="btn btn-sm btn-light border text-danger d-flex align-items-center gap-1 rounded-2"
                              style={{ fontSize: "12.5px" }}
                              title="Delete category"
                              disabled={busyId === item.id}
                              onClick={() => deleteCategory(item)}
                            >
                              <FaTrash size={11} /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
